import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSidebar } from '../context/SidebarContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Home, Ticket, Users, Settings, LogOut, Menu, ChevronLeft, ChevronRight, Plus, Moon, Sun } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import NotificationBell from './NotificationBell';
import ChatbotFAB from './ChatbotFAB';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { sidebarCollapsed, setSidebarCollapsed } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileSheetOpen, setMobileSheetOpen] = React.useState(false);
  const [isLargeScreen, setIsLargeScreen] = React.useState(window.innerWidth >= 1024);

  React.useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getSidebarMargin = () => {
    if (!isLargeScreen) return '0px';
    return sidebarCollapsed ? '64px' : '208px';
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: Home },
    { path: '/tickets', label: 'All Tickets', icon: Ticket },
    { path: '/my-tickets', label: 'My Tickets', icon: Ticket },
  ];

  // Client users have different navigation
  if (user?.role === 'Client') {
    navItems.length = 0;
    navItems.push(
      { path: '/tickets', label: 'My Tickets', icon: Ticket },
      { path: '/tickets/new', label: 'Create Ticket', icon: Plus }
    );
  } else {
    navItems[0] = { path: '/my-dashboard', label: 'My Dashboard', icon: Home };
    navItems[1] = { path: '/dashboard', label: 'Team Dashboard', icon: Ticket };
    navItems[2] = { path: '/all-tickets', label: 'All Tickets', icon: Ticket };
    navItems[3] = { path: '/my-tickets', label: 'My Tickets', icon: Ticket };
    
    if (user?.role === 'Admin' || user?.role === 'Manager') {
      navItems.push({ path: '/master-setup', label: 'Master Setup', icon: Settings });
      navItems.push({ path: '/users', label: 'Users', icon: Users });
    }
    
    if (user?.role === 'Admin') {
      navItems.push({ path: '/admin/settings', label: 'System Settings', icon: Settings });
    }
  }

  const NavLink = ({ item, mobile = false }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;

    const handleClick = () => {
      if (mobile) {
        setMobileSheetOpen(false);
      }
    };

    return (
      <Link
        to={item.path}
        onClick={handleClick}
        data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
        className={`flex items-center gap-2.5 rounded-lg font-medium transition-all duration-200 ${
          isActive
            ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-md shadow-indigo-500/25'
            : isDark
            ? 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        } ${mobile ? 'px-3.5 py-2.5 text-sm' : sidebarCollapsed ? 'justify-center p-2' : 'px-3.5 py-2 text-sm'}`}
      >
        <Icon size={sidebarCollapsed && !mobile ? 16 : mobile ? 18 : 16} strokeWidth={2.5} className="flex-shrink-0" />
        {!sidebarCollapsed && !mobile && <span>{item.label}</span>}
        {mobile && <span>{item.label}</span>}
      </Link>
    );
  };

  return (
    <div className="min-h-screen w-full transition-colors duration-300 bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:bg-slate-950 dark:bg-gradient-to-br dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 transition-colors duration-300 dark:bg-slate-900/80 dark:border-slate-800/60 bg-white/80 border-slate-200/60 backdrop-blur-lg border-b shadow-sm">
        <div className="flex items-center justify-between px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 md:py-3">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            {/* Mobile Menu */}
            <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" className="h-9 w-9 sm:h-10 sm:w-10">
                  <Menu size={18} className="sm:w-5 sm:h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 sm:w-72 p-0 dark:bg-slate-900 dark:border-slate-800 bg-white">
                <div className="flex flex-col gap-1 mt-6">
                  {navItems.map((item) => (
                    <NavLink key={item.path} item={item} mobile />
                  ))}
                </div>
              </SheetContent>
            </Sheet>

            <Link to={user?.role === 'Client' ? '/tickets' : '/my-dashboard'} className="flex items-center gap-2 sm:gap-2.5 md:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-indigo-600 via-indigo-500 to-blue-500 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/30 flex-shrink-0">
                <span className="text-white font-bold text-xs sm:text-sm">PD</span>
              </div>
              <h1 className="text-base sm:text-lg md:text-xl font-bold tracking-tight transition-colors duration-300 text-slate-900 dark:text-white">PulseDesk</h1>
            </Link>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 md:gap-3">
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleTheme}
                    className="h-9 sm:h-10 w-9 sm:w-10 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
                    data-testid="theme-toggle"
                  >
                    {isDark ? (
                      <Sun size={18} className="sm:w-5 sm:h-5" />
                    ) : (
                      <Moon size={18} className="sm:w-5 sm:h-5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{isDark ? 'Light Mode' : 'Dark Mode'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <NotificationBell />
            <ChatbotFAB />
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 h-9 sm:h-10 px-2 sm:px-3 text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="user-menu">
                  <Avatar className="h-7 w-7 sm:h-8 sm:w-8">
                    <AvatarFallback className="bg-blue-500 text-white text-xs sm:text-sm">
                      {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline text-xs sm:text-sm font-medium truncate max-w-xs">{user?.full_name || user?.username}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white dark:bg-slate-900 dark:border-slate-700 border-slate-200">
                <DropdownMenuLabel className="bg-slate-50 dark:bg-slate-800/70">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs sm:text-sm font-semibold truncate text-slate-900 dark:text-white">{user?.full_name || user?.username}</span>
                    <span className="text-xs text-slate-600 dark:text-slate-300 truncate">{user?.email}</span>
                    <span className="text-xs text-blue-700 dark:text-blue-300 font-medium mt-0.5">{user?.role}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="dark:bg-slate-700" />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-red-600 dark:text-red-400 cursor-pointer text-xs sm:text-sm hover:bg-red-50 dark:hover:bg-red-950">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex flex-col">
        {/* Header stays here */}
        
        {/* Content area with fixed sidebar */}
        <div className="flex flex-1">
        {/* Sidebar - Desktop */}
        <aside
          className={`hidden lg:block border-r transition-all duration-300 dark:border-slate-800/60 dark:bg-slate-900/60 border-slate-200/60 bg-white/60 backdrop-blur-sm fixed top-[57px] left-0 ${
            sidebarCollapsed ? 'w-16' : 'w-52'
          } h-[calc(100vh-57px)] overflow-y-auto`}
        >
          <div className="flex flex-col h-full">
            {/* Scrollable nav container */}
            <div className={`flex-1 space-y-1 ${sidebarCollapsed ? 'p-2' : 'p-3'}`}>
              <TooltipProvider delayDuration={300}>
                <nav className="space-y-1">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    const linkContent = (
                      <Link
                        key={item.path}
                        to={item.path}
                        data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                        className={`flex items-center gap-2.5 rounded-lg font-medium transition-all duration-200 ${
                          isActive
                            ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-md shadow-indigo-500/25'
                            : isDark
                            ? 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                        } ${sidebarCollapsed ? 'justify-center p-2' : 'px-3.5 py-2 text-sm'}`}
                      >
                        <Icon size={16} strokeWidth={2.5} className="flex-shrink-0" />
                        {!sidebarCollapsed && <span className="text-sm truncate">{item.label}</span>}
                      </Link>
                    );

                    if (sidebarCollapsed) {
                      return (
                        <Tooltip key={item.path}>
                          <TooltipTrigger asChild>
                            {linkContent}
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p className="text-xs">{item.label}</p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    }

                    return linkContent;
                  })}
                </nav>
              </TooltipProvider>
            </div>

            {/* Collapse button fixed at bottom */}
            <div className={`${sidebarCollapsed ? 'p-2' : 'p-3'} border-t transition-colors duration-300 dark:border-slate-800/40 border-slate-200/40`}>
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                      className={`w-full transition-all hover:bg-slate-100 dark:hover:bg-slate-800/50 text-xs sm:text-sm ${
                        sidebarCollapsed ? 'justify-center p-2' : 'justify-start px-3.5 py-2'
                      }`}
                    >
                      {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                      {!sidebarCollapsed && <span className="pl-2">Collapse</span>}
                    </Button>
                  </TooltipTrigger>
                  {sidebarCollapsed && (
                    <TooltipContent side="right">
                      <p className="text-xs">Expand</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main 
          style={{ marginLeft: getSidebarMargin() }}
          className="flex-1 p-3 sm:p-4 md:p-6 transition-all duration-300 overflow-auto dark:bg-slate-950"
        >
          {children}
        </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;